import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Pencil, KeyRound, Ban, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { DashLayout } from "@/components/DashLayout";
import { WalletCards } from "@/components/WalletCards";
import { TxTable, PayoutTable, Pill } from "@/components/Tables";
import { WalletFilter } from "@/components/WalletFilter";
import { WalletAdjustForm, TransferForm, QrUpiForm, EditProfileDialog, PasswordDialog } from "@/pages/admin/UserForms";
import { api, errMsg, fmtDate } from "@/lib/api";
import { ADMIN_NAV } from "@/pages/navs";

export default function AdminUserDetail() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  const [txns, setTxns] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [tab, setTab] = useState("transactions");
  const [edit, setEdit] = useState(false);
  const [pw, setPw] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/users/${id}`).then((r) => setU(r.data));
    api.get("/admin/transactions", { params: { user_id: id } }).then((r) => setTxns(r.data));
    api.get("/admin/payouts", { params: { user_id: id } }).then((r) => setPayouts(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggleBlock = async () => {
    const blocked = u.status !== "blocked";
    if (!window.confirm(blocked ? "Block this merchant?" : "Unblock this merchant?")) return;
    try { setU((await api.post(`/admin/users/${id}/status`, { blocked })).data); toast.success(blocked ? "Merchant blocked" : "Merchant unblocked"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!u) return <DashLayout nav={ADMIN_NAV} title="Merchant"><div className="text-slate-400">Loading...</div></DashLayout>;
  const blocked = u.status === "blocked";

  return (
    <DashLayout nav={ADMIN_NAV} title={u.name} subtitle={u.email}>
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-[#2563EB]" data-testid="back-to-users"><ArrowLeft className="h-4 w-4" /> All merchants</Link>
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-6 lg:flex-row lg:items-center">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-xl font-bold text-[#0A1F44]" data-testid="detail-user-name">{u.name}</h2>
            <Pill data-testid="detail-user-status" className={blocked ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}>{u.status}</Pill>
          </div>
          <div className="mt-2 grid gap-x-6 gap-y-1 text-sm text-slate-500 sm:grid-cols-2">
            <span>Business: <b className="text-slate-700">{u.business_name || "-"}</b></span>
            <span>Phone: <b className="text-slate-700">{u.phone || "-"}</b></span>
            <span>Username: <b className="font-mono text-slate-700">{u.username}</b></span>
            <span>Joined: <b className="text-slate-700">{fmtDate(u.created_at)}</b></span>
          </div>
          <a href={`/pay/${u.username}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#2563EB]" data-testid="detail-pay-link">/pay/{u.username} <ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEdit(true)} data-testid="edit-profile-btn" className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"><Pencil className="h-4 w-4" /> Edit profile</button>
          <button onClick={() => setPw(true)} data-testid="change-password-btn" className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"><KeyRound className="h-4 w-4" /> Change password</button>
          <button onClick={toggleBlock} data-testid="toggle-block-btn" className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white ${blocked ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
            {blocked ? <><CheckCircle2 className="h-4 w-4" /> Unblock</> : <><Ban className="h-4 w-4" /> Block</>}
          </button>
        </div>
      </div>
      <WalletCards user={u} />
      <div className="grid gap-4 lg:grid-cols-3">
        <WalletAdjustForm uid={id} onDone={load} />
        <TransferForm uid={id} onDone={load} />
        <QrUpiForm key={`${u.qr_url}-${u.upi_id}`} user={u} onDone={load} />
      </div>
      <WalletFilter value={tab} onChange={setTab} options={["transactions", "payouts"]} prefix="detail-tab" />
      {tab === "transactions" ? <TxTable rows={txns} /> : <PayoutTable rows={payouts} />}
      {edit && <EditProfileDialog user={u} open={edit} setOpen={setEdit} onDone={load} />}
      <PasswordDialog uid={id} open={pw} setOpen={setPw} />
    </DashLayout>
  );
}
